import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import { Box, Button, Chip, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material'
import { blue, red } from '@mui/material/colors'
import EventIcon from '@mui/icons-material/Event'
import EventBusyIcon from '@mui/icons-material/EventBusy'
import CancelIcon from '@mui/icons-material/Cancel'
import CloseIcon from '@mui/icons-material/Close'
import UndoIcon from '@mui/icons-material/Undo'

import T from 'T'
import { formatDateToFEFormatDateFns } from 'utils/date'
import HHBaseDialog from 'components/common/HHBaseDialog'

const DATE_TYPE = {
  START: 'start',
  END: 'end',
}

const RentalFeeDateChips = ({
  startDate,
  endDate,
  isStartDateDeletable = true,
  isEndDateDeletable = true,
  showEditModeActions = false,
  onClearStartDate,
  onClearEndDate,
  onExitEditMode = null,
  onRevertEditMode = null,
}) => {
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false)
  const [dateTypeToDelete, setDateTypeToDelete] = useState(null)

  const formattedStartDate = startDate ? formatDateToFEFormatDateFns(startDate) : null
  const formattedEndDate = endDate ? formatDateToFEFormatDateFns(endDate) : null

  const handleOpenConfirmDialog = useCallback((event, dateType) => {
    event.stopPropagation()
    setDateTypeToDelete(dateType)
    setIsConfirmDialogOpen(true)
  }, [])

  const handleCloseConfirmDialog = useCallback(() => {
    setIsConfirmDialogOpen(false)
    setDateTypeToDelete(null)
  }, [])

  const handleConfirmDelete = useCallback(() => {
    if (dateTypeToDelete === DATE_TYPE.START) {
      onClearStartDate()
    } else if (dateTypeToDelete === DATE_TYPE.END) {
      onClearEndDate()
    }
    handleCloseConfirmDialog()
  }, [dateTypeToDelete, onClearStartDate, onClearEndDate, handleCloseConfirmDialog])

  const handleClearStartDate = useCallback(
    event => {
      handleOpenConfirmDialog(event, DATE_TYPE.START)
    },
    [handleOpenConfirmDialog]
  )

  const handleClearEndDate = useCallback(
    event => {
      handleOpenConfirmDialog(event, DATE_TYPE.END)
    },
    [handleOpenConfirmDialog]
  )

  if (!formattedStartDate && !formattedEndDate) {
    return null
  }

  return (
    <>
      <Box display="flex" flexDirection="column" gap={1}>
        <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
          {formattedStartDate && (
            <Chip
              icon={<EventIcon sx={{ fontSize: 16 }} />}
              label={formattedStartDate}
              size="small"
              variant="outlined"
              deleteIcon={isStartDateDeletable ? <CancelIcon sx={{ fontSize: 16 }} /> : undefined}
              onDelete={isStartDateDeletable ? handleClearStartDate : undefined}
              sx={{
                borderColor: blue[500],
                color: blue[500],
                '& .MuiChip-icon': {
                  color: blue[500],
                },
                '& .MuiChip-deleteIcon': {
                  color: blue[500],
                  '&:hover': {
                    color: blue[100],
                  },
                },
              }}
            />
          )}
          {formattedEndDate && (
            <Chip
              icon={<EventBusyIcon sx={{ fontSize: 16 }} />}
              label={formattedEndDate}
              size="small"
              variant="outlined"
              deleteIcon={isEndDateDeletable ? <CancelIcon sx={{ fontSize: 16 }} /> : undefined}
              onDelete={isEndDateDeletable ? handleClearEndDate : undefined}
              sx={{
                borderColor: red[500],
                color: red[500],
                '& .MuiChip-icon': {
                  color: red[500],
                },
                '& .MuiChip-deleteIcon': {
                  color: red[500],
                  '&:hover': {
                    color: red[100],
                  },
                },
              }}
            />
          )}
        </Box>
        {showEditModeActions && (onExitEditMode || onRevertEditMode) && (
          <Box display="flex" gap={1} flexWrap="wrap">
            {onExitEditMode && (
              <Button
                size="small"
                variant="outlined"
                color="inherit"
                startIcon={<CloseIcon />}
                onClick={onExitEditMode}
                sx={{ textTransform: 'none' }}
              >
                {T.EXIT_EDIT_MODE}
              </Button>
            )}
            {onRevertEditMode && (
              <Button
                size="small"
                variant="outlined"
                color="inherit"
                startIcon={<UndoIcon />}
                onClick={onRevertEditMode}
                sx={{ textTransform: 'none' }}
              >
                {T.REVERT_EDIT_CHANGES}
              </Button>
            )}
          </Box>
        )}
      </Box>

      <HHBaseDialog open={isConfirmDialogOpen} onClose={handleCloseConfirmDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Clear date</DialogTitle>
        <DialogContent>
          <DialogContentText>Are you sure you want to clear this date? You will lose any unsaved changes.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmDialog} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} variant="contained" color="error">
            Clear
          </Button>
        </DialogActions>
      </HHBaseDialog>
    </>
  )
}

RentalFeeDateChips.propTypes = {
  startDate: PropTypes.instanceOf(Date),
  endDate: PropTypes.instanceOf(Date),
  isStartDateDeletable: PropTypes.bool,
  isEndDateDeletable: PropTypes.bool,
  showEditModeActions: PropTypes.bool,
  onClearStartDate: PropTypes.func.isRequired,
  onClearEndDate: PropTypes.func.isRequired,
  onExitEditMode: PropTypes.func,
  onRevertEditMode: PropTypes.func,
}

export default RentalFeeDateChips
